import React, {useEffect} from "react";
import {useHistory, useLocation} from "react-router-dom";
import styled from "styled-components";
import {ReactComponent as GradeIcon} from "../../resources/icons/grade.svg";
import AppStorage, {StorageKey} from "../../util/AppStorage";
import {UserType} from "../../constants/enums";
import {useGlobalState} from "../../util/GlobalState";
import {useMenuDataContext} from "../../contexts/MenuDataContext";
import {isSchoolYearDisabledRoute, normalizePathname} from "../../util/PathnameHelpers";

const StyledGradeIcon = styled(GradeIcon)`
    width: 20px;
    height: 20px;
    display: block;
    flex-shrink: 0;
    color: white;
`;

export function useDisableMentorSchoolYear() {
    const {schoolYearsAndGradesData, setGradeOptions} = useMenuDataContext();
    const history = useHistory();
    const location = useLocation();
    const [, setSelectedSchoolYear] = useGlobalState("selectedSchoolYear");
    const [, setSelectedGrade] = useGlobalState("selectedGrade");
    const [, setSelectedPupil] = useGlobalState("selectedPupil");

    const normalizedPathname = normalizePathname(location.pathname);
    const disableSchoolYear = AppStorage.get(StorageKey.USER_TYPE) === UserType.MENTOR && isSchoolYearDisabledRoute(normalizedPathname);

    useEffect(() => {
        if (AppStorage.get(StorageKey.USER_TYPE) !== UserType.MENTOR) return;
        if (!isSchoolYearDisabledRoute(normalizedPathname)) return;
        if (!schoolYearsAndGradesData?.length) return;

        const firstYear = schoolYearsAndGradesData[0];
        const firstGradeId = firstYear?.grades?.[0]?.value;
        const previousSchoolYearId = AppStorage.get(StorageKey.SCHOOL_YEAR_ID);
        const currentGradeId = AppStorage.get(StorageKey.GRADE_ID);


        const newGradeOptions = firstYear?.grades?.map((grade) => ({
            value: grade.value,
            label: grade.label,
            icon: <StyledGradeIcon/>,
        }));

        // Mentor was already on the first school year from the data. Disable the year dropdown and refresh grade options, but keep the selected grade and pupil.
        if (previousSchoolYearId === firstYear?.value) {
            AppStorage.set(StorageKey.SCHOOL_YEAR_ID, firstYear.value);
            setSelectedSchoolYear(firstYear.value);
            setGradeOptions(newGradeOptions);

            // Keep the current grade if it exists in this school year’s list
            if (firstYear?.grades?.some((g) => g.value === currentGradeId)) {
                setSelectedGrade(currentGradeId);
            } else if (firstGradeId) {
                AppStorage.set(StorageKey.GRADE_ID, firstGradeId);
                setSelectedGrade(firstGradeId);
            }
            return;
        }

        // When the mentor has another school year selected, force first year + first grade, clear pupil context, then leave pupil schedule URLs for the overview.
        AppStorage.set(StorageKey.SCHOOL_YEAR_ID, firstYear.value);
        setSelectedSchoolYear(firstYear.value);
        if (firstGradeId) {
            AppStorage.set(StorageKey.GRADE_ID, firstGradeId);
            setSelectedGrade(firstGradeId);
        }

        AppStorage.remove(StorageKey.PUPIL_ID);
        AppStorage.remove(StorageKey.PUPIL_TITLE);
        setSelectedPupil(null);

        setGradeOptions(newGradeOptions);

        // After a forced year reset, the user can stay on the overview page. When on the pupil's schedule page, redirect to the overview.
        if (!["/dashboard", "/mentor/planagenda"].includes(normalizedPathname)) {
            history.replace("/mentor/planagenda");
        }
    // eslint-disable-next-line
    }, [normalizedPathname, schoolYearsAndGradesData, history]);

    return disableSchoolYear;
}
